import React from 'react';
import { authenticatedUser } from 'header-scripts';
import { TranslateFunction } from 'react-utilities';
import { escapeHtml } from 'core-utilities';
import { Thumbnail2d, ThumbnailTypes, DefaultThumbnailSize } from 'roblox-thumbnails';
import itemCardConstants from '../constants/itemCardConstants';
import itemCardUtils, { TItemCardRestrictions, TItemStatus } from '../utils/itemCardUtils';
import { getNumberFormat } from '../../common/parsingUtils';

export function ItemCard({
  id,
  name,
  type,
  creatorName,
  creatorType,
  creatorTargetId,
  price,
  lowestPrice,
  priceStatus,
  premiumPricing,
  unitsAvailableForConsumption,
  itemStatus,
  itemStatusLabels,
  itemRestrictions,
  itemRestrictionLabels,
  url,
  translate
}: {
  id: number;
  name: string;
  type: string;
  creatorName: string;
  creatorType: string;
  creatorTargetId: number;
  price: number;
  lowestPrice: number;
  priceStatus: string;
  premiumPricing: number;
  unitsAvailableForConsumption: number;
  itemStatus: Array<string>;
  itemStatusLabels: Array<TItemStatus>;
  itemRestrictions: Array<string>;
  itemRestrictionLabels: TItemCardRestrictions;
  url: string;
  translate: TranslateFunction;
}): JSX.Element {
  const hasSecondaryInfo = () => {
    return (
      creatorTargetId !== itemCardConstants.robloxSystemUserId ||
      unitsAvailableForConsumption > 0 ||
      (price > 0 && lowestPrice > 0)
    );
  };

  const getPriceForItem = () => {
    if (authenticatedUser.isPremiumUser && premiumPricing >= 0) {
      return premiumPricing;
    }
    if (lowestPrice >= 0) {
      return lowestPrice;
    }
    return price;
  };

  const shouldShowPremiumIcon = () => {
    return premiumPricing >= 0;
  };

  return (
    <div className='list-item item-card grid-item-container item-card-container' key={name}>
      <a href={url} target='_self' className='item-card-link'>
        <div className='item-card-link'>
          <div className='item-card-thumb-container'>
            <Thumbnail2d
              type={
                type === 'CatalogBundle'
                  ? ThumbnailTypes.bundleThumbnail
                  : ThumbnailTypes.assetThumbnail
              }
              targetId={id}
              size={DefaultThumbnailSize}
            />
            {itemStatus !== undefined && itemStatus.length > 0 && (
              <div className=' item-cards-stackable'>
                <div className='asset-status-icon'>
                  {itemStatusLabels.map((status: TItemStatus) => {
                    return (
                      <div
                        className={`${status.isIcon ? 'has-icon' : ''} ${status.class}`}
                        key={status.type}>
                        {status.isIcon && <span className={status.type} />}
                        {status.label !== undefined && <span>{translate(status.label)}</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {itemRestrictions.length > 0 &&
              itemRestrictionLabels.itemRestrictionIcon !== undefined && (
                <span className={`restriction-icon ${itemRestrictionLabels.itemRestrictionIcon}`} />
              )}
          </div>
        </div>
        <div className='item-card-caption'>
          <div className='item-card-name-link'>
            <div className='item-card-name' title={name}>
              {shouldShowPremiumIcon() && <span className='icon-premium-small' />}
              {name}
            </div>
          </div>
          <div className='item-card-secondary-info text-secondary'>
            {unitsAvailableForConsumption > 0 && (
              <div className='text-overflow item-card-label'>
                <span>{translate('Label.Card.Remaining')}</span>
                <span>{getNumberFormat(unitsAvailableForConsumption)}</span>
              </div>
            )}
            {creatorName !== undefined &&
              creatorTargetId !== undefined &&
              creatorType !== undefined &&
              !(creatorTargetId === 1 && creatorType === 'User') && (
                <div className='text-overflow item-card-label'>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: translate('Label.ByCreatorLink', {
                        linkStart: `<a target=_self class='creator-name text-link' href='${itemCardUtils.getProfileLink(
                          creatorTargetId,
                          creatorType,
                          escapeHtml(creatorName)
                        )}'>`,
                        linkEnd: '</a>',
                        creator: creatorName
                      })
                    }}
                  />
                </div>
              )}
            {lowestPrice >= 0 && (
              <div className='text-overflow item-card-label'>
                <span>{translate('Label.Card.PriceWas')}</span>
                <span className='icon-robux-gray-16x16' />
                <span className='strike-through'>{getNumberFormat(price)}</span>
              </div>
            )}
          </div>
          {price === 0 && priceStatus !== undefined && (
            <div
              className={`text-overflow item-card-price font-header-2 text-subheader ${
                hasSecondaryInfo() ? 'margin-top-none' : ''
              }`}>
              <span className='text text-label text-robux-tile'>{priceStatus}</span>
            </div>
          )}
          {getPriceForItem() > 0 && (
            <div
              className={`text-overflow item-card-price font-header-2 text-subheader ${
                hasSecondaryInfo() ? 'margin-top-none' : ''
              }`}>
              <span className='icon icon-robux-16x16' />
              <span className='text-robux-tile'>{getNumberFormat(getPriceForItem())}</span>
            </div>
          )}
        </div>
      </a>
    </div>
  );
}

export default ItemCard;
